import { DecisionModalService } from '../decision-modal/decision-modal.service';
import { ORDER_TYPE_FIND_SUCCESS, ORDER_TYPE_UPDATE } from './../../../store/actions/order-type.action';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Observable, of, filter, forkJoin } from 'rxjs';
import { switchMap, catchError, take, map, distinctUntilChanged } from 'rxjs/operators';
import { ModalController } from '@ionic/angular';
import { WorkDay, Store as StoreModel } from '@core/models';
import { Store } from '@ngrx/store';
import { AppState } from '@store/reducers';
import { IResponseSuccess } from '@core/interfaces';
import { selectStore } from '@store/selectors';
import { Router } from '@angular/router';
import { selectOrderTypeList } from '@store/selectors/order-type.selectors';
import { selectLocalChannelId, selectLocalChannelSesion } from '@store/selectors/local-channel.selectors';
import { OrderType } from '@core/models/orden/order-type';
import { OrderTypeEnum } from '@core/enums';
import { OrderTypeService } from '../order-type/order-type.service';
import { selectOrderType } from '@store/selectors/order-type.selectors';
import { SwitchService } from '../switch.service';
import { AVAILABILITY_FIND_ALL, LOCAL_CHANNEL_RESET_STATE, STORE_FIND_SUCCESS } from '@store/actions';
import { NotificationEnum } from '@shared/components/notification/enum/notification.enum';

@Injectable({
	providedIn: 'root',
})
export class WorkDayService {
  isModalShowing = false;
	store$: Observable<StoreModel | null>;

	constructor(
		private http: HttpClient,
		private modalCtrl: ModalController,
		private store: Store<AppState>,
		private router: Router,
		private orderTypeService: OrderTypeService,
		private switchService: SwitchService,
		private decisionModalService: DecisionModalService,
	) {
		this.store$ = this.store.select(selectStore);
	}

	getWorkDay(localId: string): Observable<IResponseSuccess<WorkDay>> {
		const fechaActual = new Date().toLocaleDateString('en-CA');

		return this.store$.pipe(
			take(1),
			filter(storeData => !!storeData),
			switchMap(storeData => {
				return this.http.get<IResponseSuccess<WorkDay>>(`${environment.microConfiguraciones}/dialaboral`, {
					headers: {
						authorization: storeData.apiKey,
					},
					params: {
						localId,
						fechaApertura: fechaActual as string,
						fechaCierre: fechaActual as string,
					},
				});
			})
		);
	}

	obtenerWorkDayActual(localId: string): Observable<WorkDay | null> {
		const fechaActual = new Date().toLocaleDateString('en-CA');

		const params = {
			localId,
			fechaApertura: fechaActual,
			fechaCierre: fechaActual,
		};

		return this.store$.pipe(
			filter(storeData => !!storeData),
			distinctUntilChanged(),
			switchMap(storeData => {
				return forkJoin([
					this.http.get<IResponseSuccess<WorkDay>>(`${environment.microConfiguraciones}/dialaboral`, {
						params,
						headers: { authorization: storeData.apiKey },
					}),
					this.validateOrderType(),
				]).pipe(
					map(([{ data }]) => this.checkStoreClosed(data)),
					take(1)
				);
			}),
			take(1)
		);
	}

	checkStoreClosed(response: WorkDay[]): WorkDay | null {
		const diaAbierto = response.find((dia: WorkDay) => !!dia.estaAbierto) ?? null;

		if (!diaAbierto) {
			this.handleStoreClosed();

			return null;
		}

		return diaAbierto;
	}

	validateOrderType(): Observable<void> {
		return this.store.select(selectLocalChannelId).pipe(
			take(1),
			filter(selectLocalChannelId => !!selectLocalChannelId),
			map((localChannelId: string | null) => {
				if (localChannelId) {
					this.orderTypeService.find(localChannelId).subscribe(resp => {
						if (resp && resp.success && Array.isArray(resp.data)) {
							const disponibilidad = this.orderTypeService.checkOrderTypeAvailabilityByCurrentTime(resp.data);
							this.store
								.select(selectOrderType)
								.pipe(
									filter((selectedOrderType) => !!selectedOrderType),
									take(1)
								).subscribe((selectedOrderType: string) => {
									const deliveryDisponible = disponibilidad.delivery;
									const pickupDisponible = disponibilidad.pickup;

									if (!deliveryDisponible && !pickupDisponible) {
										this.handleStoreClosed();
									} else if (
										(selectedOrderType == OrderTypeEnum.Delivery && !deliveryDisponible) ||
										(selectedOrderType == OrderTypeEnum.Pickup && !pickupDisponible)
									) {
										const otroTipo =
											selectedOrderType == OrderTypeEnum.Delivery ? OrderTypeEnum.Pickup : OrderTypeEnum.Delivery;

										this.decisionModalService.showClosedModal({
											title:
												otroTipo !== OrderTypeEnum.Pickup
													? NotificationEnum.OrderTypeNotAvailableTitlePickup || 'Type of order not available'
													: NotificationEnum.OrderTypeNotAvailableTitleDelivery || 'Type of order not available',
											question:
												otroTipo === OrderTypeEnum.Pickup
													? NotificationEnum.OrderTypeNotAvailableMessagePickup ||
														'The selected order type is not available at this time. Do you want to change to ' +
															otroTipo
													: NotificationEnum.OrderTypeNotAvailableMessageDelivery ||
														'The selected order type is not available at this time. Do you want to change to ' +
															otroTipo,
											showOkBtn: true,
											okText:
												otroTipo === OrderTypeEnum.Pickup
													? NotificationEnum.ChangeToOrderTypePickup || 'Change to {orderType}'
													: NotificationEnum.ChangeToOrderTypeDelivery || 'Change to {orderType}',
											cancelBtn: true,
											cancelText: NotificationEnum.LeaveTheStore || 'Leave the store',
											secondBtn: false,
											okHandler: async () => {
												this.isModalShowing = false;
												this.store.dispatch(ORDER_TYPE_UPDATE({ orderType: otroTipo }));
												this.switchService.setServiceType(otroTipo);
												await this.modalCtrl.dismiss();
											},
											cancelHandler: async () => {
												this.store.dispatch(STORE_FIND_SUCCESS({ store: null }));
												this.store.dispatch(AVAILABILITY_FIND_ALL());
												this.store.dispatch(ORDER_TYPE_FIND_SUCCESS({ orderTypeList: null }));
												this.store.dispatch(LOCAL_CHANNEL_RESET_STATE());
												this.router.navigate(['stores']);
												this.isModalShowing = false;

												await this.modalCtrl.dismiss();
											},
										});
									}
								});
						}
					});
				}
			})
		);
	}

	public handleStoreClosed(): Promise<null> {
		if (this.isModalShowing) return Promise.resolve(null);
		this.isModalShowing = true;
		return this.decisionModalService
			.showClosedModal({
				title: NotificationEnum.StoreClosedTitle || 'Store closed',
				question: NotificationEnum.StoreClosedMessage || 'We are currently closed. Please come back later.',
				showOkBtn: true,
				okText: NotificationEnum.LeaveTheStore || 'Leave the store',
				okHandler: async () => {
					this.store.dispatch(STORE_FIND_SUCCESS({ store: null }));
					this.store.dispatch(ORDER_TYPE_FIND_SUCCESS({ orderTypeList: null }));
					this.store.dispatch(LOCAL_CHANNEL_RESET_STATE());
					this.router.navigate(['stores']);
					this.isModalShowing = false;
					await this.modalCtrl.dismiss();
				},
				cancelHandler: async () => {
					this.store.dispatch(STORE_FIND_SUCCESS({ store: null }));
					this.store.dispatch(ORDER_TYPE_FIND_SUCCESS({ orderTypeList: null }));
					this.store.dispatch(LOCAL_CHANNEL_RESET_STATE());
					this.router.navigate(['stores']);
					this.isModalShowing = false;
					await this.modalCtrl.dismiss();
				},
				cancelBtn: false,
				secondBtn: false,
			})
			.then(() => null);
	}

	validateStoreOpen(localChannelIdParam?: string | null): Observable<{ delivery: boolean; pickup: boolean }> {
		return this.store.select(selectOrderTypeList).pipe(
			take(1),
			switchMap((orderTypeList: OrderType[] | null) => {
				if (orderTypeList && orderTypeList.length > 0) {
					return of(this.orderTypeService.checkOrderTypeAvailabilityByCurrentTime(orderTypeList));
				}

				if (localChannelIdParam) {
					return this.orderTypeService.find(localChannelIdParam).pipe(
						map(resp => {
							if (resp && resp.success && Array.isArray(resp.data)) {
								return this.orderTypeService.checkOrderTypeAvailabilityByCurrentTime(resp.data);
							}
							return { delivery: false, pickup: false };
						}),
						catchError(() => of({ delivery: false, pickup: false }))
					);
				}

				return this.store.select(selectLocalChannelSesion).pipe(
					take(1),
					switchMap(localChannel => {
						const channelId = localChannel?.[0]?.idLocalCanal || localChannel?.[0]?.localCanalId || null;
						if (!channelId) return of({ delivery: false, pickup: false });
						return this.orderTypeService.find(channelId).pipe(
							map(resp => {
								if (resp && resp.success && Array.isArray(resp.data)) {
									return this.orderTypeService.checkOrderTypeAvailabilityByCurrentTime(resp.data);
								}
								return { delivery: false, pickup: false };
							}),
							catchError(() => of({ delivery: false, pickup: false }))
						);
					}),
					catchError(() => of({ delivery: false, pickup: false }))
				);
			})
		);
	}
}
